import * as React from 'react';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import type { IPcEditorAppServices } from '@src/slide/application/entry/toc/editor/pc/pc-editor-app-services.interface';
import type { IElementBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import { getAllChildModels } from '@src/slide/domain/ui-model/element-modeles/property/utils/merge-property';
import type { ICTPoint2DDelta } from '@tencent/slide-sdk-model';
import { observer } from 'mobx-react';
import { Component } from 'react';
import { ServicesContext } from '../../../context';
import CommentAnchor from '../components/comment-anchor';
import ElementCommentBorder from '../components/element-comment-border';
import { getCacConfigByKey } from '@src/slide/infrastructure/module/dependent/config/tob/app-config';

interface ICommentBorderProps {
    scale: number;
    slideAreaSize: {
        width: number;
        height: number;
        top: number;
        left: number;
    };
}

interface IActiveElementInfo {
    hoverId?: string;
    activeId?: string;
    anchorPosition?: ICTPoint2DDelta;
}

@observer
class CommentBorder extends Component<ICommentBorderProps> {
    public static contextType = ServicesContext;

    public componentDidMount() {
        // 首次渲染时commentManager尚未初始化完成，初始化完成需重新触发一次
        this.getCommentManager()?.promise()
            .then(() => {
                this.setState({});
            });
    }

    public render() {
        const commentManager = this.getCommentManager().dangerousInstance;
        if (commentManager?.shouldHideComment() || !getCacConfigByKey('feature.comment', false)) return null;
        const { scale } = this.props;
        const { currentSlide } = this.getSelectionService();
        if (this.isOperating()) return null;
        if (!currentSlide?.commentGroupInfoMap?.size) return null;
        const activeElementInfo = this.getActiveElement();
        const hoverId = activeElementInfo?.hoverId;
        const activeId = activeElementInfo?.activeId;
        const needShowCommentAnchor = hoverId || activeId
            || commentManager?.isCommentAnchorShown
            || commentManager?.isCommentDrawerShown;
        if (!needShowCommentAnchor) return null;
        return <div
            className='comment-border'
            style={{
                position: 'absolute',
                transformOrigin: 'left top',
                transform: `scale(${1 / scale})`,
                width: `${this.props.slideAreaSize.width * scale}px`,
                height: `${this.props.slideAreaSize.height * scale}px`,
                outline: activeId === currentSlide?.id ? '2px dashed #ffab00' : '',
            }}
        >
            {this.renderSlideCommentAnchor(activeElementInfo)}
            {this.renderElementBorder(activeElementInfo)}
        </div>;
    }

    private renderSlideCommentAnchor = (activeElementInfo?: IActiveElementInfo) => {
        const { hoverId, activeId, anchorPosition } = activeElementInfo ?? {};
        const { currentSlide } = this.getSelectionService();
        const slideCommentCount = this.getSlideCommentCount();
        const isActiveOrHover = hoverId === currentSlide?.id || activeId === currentSlide?.id;
        return slideCommentCount > 0
            ? <CommentAnchor
                anchorPosition={isActiveOrHover ? anchorPosition : undefined}
                count={slideCommentCount}
                isActive={hoverId === currentSlide?.id || activeId === currentSlide?.id}
                commentManager={this.getCommentManager()}
                scale={this.props.scale}
                elementId={currentSlide?.id}
            /> : null;
    };

    private renderElementBorder = (activeElementInfo?: IActiveElementInfo) => {
        const { hoverId, activeId, anchorPosition } = activeElementInfo ?? {};
        const { currentSlide } = this.getSelectionService();
        const commentManager = this.getCommentManager().dangerousInstance;
        return currentSlide?.children.map((element: IElementBaseModel) => {
            const allChildModels = getAllChildModels(element, false);
            return allChildModels.map((child: IElementBaseModel) => {
                if (!child.id) return null;
                const commentCount = commentManager?.commentGroupIdsMap.get(child.id)?.length;
                if (!commentCount) return null;
                return <ElementCommentBorder
                    elementModel={child}
                    scale={this.props.scale}
                    isActive={activeId === child.id}
                    isHover={hoverId === child.id}
                    commentCount={commentCount}
                    anchorPosition={anchorPosition}
                    commentManager={this.getCommentManager()}
                    key={child.key}
                /> ;
            });
        });
    };

    private getSlideCommentCount = (): number => {
        const slideId = this.getSlideId();
        const commentManager = this.getCommentManager().dangerousInstance;
        if (slideId) {
            return commentManager?.commentGroupIdsMap.get(slideId)?.length ?? 0;
        }
        return 0;
    };

    /**
     * 获取当前批注侧边栏选中的批注组对应的元素
     */
    private getActiveElement = (): undefined | IActiveElementInfo => {
        const commentManager = this.getCommentManager().dangerousInstance;
        // 获取hover的元素id
        if (commentManager?.hoverGroupId) {
            const info = this.getActiveElementInfo(commentManager?.hoverGroupId);
            if (commentManager?.hoverGroupId === commentManager.activeGroupId) {
                return { activeId: info?.elementId, anchorPosition: info?.anchorPosition };
            }
            return { hoverId: info?.elementId, anchorPosition: info?.anchorPosition };
        }
        // 获取active的元素id
        if (commentManager?.activeGroupId) {
            const info = this.getActiveElementInfo(commentManager?.activeGroupId);
            return { activeId: info?.elementId, anchorPosition: info?.anchorPosition };
        }
    };

    private getActiveElementInfo = (groupId: string) => {
        const commentManager = this.getCommentManager().dangerousInstance;
        const commentGroupMap = this.getSelectionService().currentSlide?.commentGroupMap;
        if (!commentGroupMap) return;
        const commentGroup = commentGroupMap.get(groupId);
        const shapeId = commentGroup?.shapeId;
        const elementId = shapeId ? shapeId : this.getSlideId();
        if (elementId) {
            if (commentManager?.commentGroupIdsMap.get(elementId)?.includes(groupId)) {
                return {
                    elementId,
                    anchorPosition: commentGroup?.anchorPosition,
                };
            }
        }
    };

    private isOperating = (): boolean => {
        const {
            isControlValuesChanging,
            isDragging,
            isResizing,
            isRotating,
            isConnecting,
            isCroping,
        } = this.context.editingStateManager;
        return isControlValuesChanging
            || isDragging
            || isResizing
            || isRotating
            || isConnecting
            || isCroping
            || this.isPreview()
            || this.getRotateElementHandler()?.isMobileRotatingMode;
    };

    private isPreview() {
        return this.context.editorAnimPreviewManager?.isAnimPreviewing
            || this.getPreviewService()?.isPreview;
    }

    private getPreviewService() {
        return (AppServiceProvider.getInstance().getAppServices() as IPcEditorAppServices)
            .editableServiceLayer?.previewService;
    }

    private getRotateElementHandler() {
        return (AppServiceProvider.getInstance().getAppServices() as IPcEditorAppServices)
            .editableServiceLayer?.rotateElementHandler;
    }

    private getSlideId = () => this.getSelectionService().currentSlide?.id;

    private getCommentManager() {
        return (AppServiceProvider.getInstance().getAppServices() as IPcEditorAppServices)
            .viewableServiceLayer?.commentManager;
    }

    private getSelectionService() {
        return (AppServiceProvider.getInstance().getAppServices() as IPcEditorAppServices)
            .viewableServiceLayer?.selectionService;
    }
}

export default CommentBorder;
